import React, { useState, useEffect, useMemo } from "react";
import { DashboardStatsSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { TransactionApi } from "@/api/transaction.api";
import { MerchantApi } from "@/api/merchant.api";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { MetricsCard } from "@/components/ui/metrics-card";
import { Gift, CreditCard, BarChart2, Star } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useUserInfo } from "@/hooks/use-user-info";
import { isMerchantAdmin } from "@/utils/role.utils";

const PERIODS = [
  { key: "ONE_MONTH", label: "1 Month" },
  { key: "THREE_MONTHS", label: "3 Months" },
  { key: "SIX_MONTHS", label: "6 Months" },
  { key: "ONE_YEAR", label: "1 Year" },
  { key: "LIFETIME", label: "Lifetime" },
];

const MERCHANT_PIE_COLORS = [
  "#1e40af",
  "#3b82f6",
  "#93c5fd",
  "#2563eb",
  "#60a5fa",
  "#bfdbfe",
  "#1d4ed8",
  "#6366f1",
];

const STATUS_PIE_COLORS: Record<string, string> = {
  FINALIZED: "#22c55e",
  NOT_FINALIZED: "#eab308",
  REFUNDED: "#ef4444",
};

function formatTransactionStatus(status: string) {
  switch (status) {
    case "FINALIZED":
      return "Success";
    case "NOT_FINALIZED":
      return "Pending";
    case "REFUNDED":
      return "Failed";
    default:
      return status;
  }
}

// Utility to format numbers as 10k, 20k, etc.
function formatYAxisTick(value: number) {
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + "k";
  }
  return value.toString();
}

const Dashboard = () => {
  const { userInfo } = useUserInfo();
  // Prefer hook value; fall back to sessionStorage so charts don't flash before hook hydrates
  const merchantAdmin = isMerchantAdmin(
    userInfo?.roleCode ??
      (() => {
        try {
          return JSON.parse(sessionStorage.getItem("userInfo") || "{}")
            ?.roleCode;
        } catch {
          return undefined;
        }
      })(),
  );
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null); // will hold generalStatsData
  const [currency, setCurrency] = useState<any>(null);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState("THREE_MONTHS");

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      let payload: { clientId: number | null; programType: string } = {
        clientId: null,
        programType: "EARN",
      };
      try {
        const storedUserInfo = JSON.parse(
          sessionStorage.getItem("userInfo") || "{}",
        );
        if (
          storedUserInfo?.roleCode === "PROGRAM_ADMIN" &&
          storedUserInfo?.clientId
        ) {
          payload = {
            clientId: storedUserInfo.clientId,
            programType: "EARN",
          };
        }
        const res = await TransactionApi.getGeneralStats(payload);
        setStats(res?.data?.generalStatsData || null);
        setCurrency(res?.data?.currencyVO || null);
      } catch {
        setStats(null);
        setCurrency(null);
        toast.error("Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    fetchStats();

    async function fetchMerchants() {
      // Charts that need merchant names are hidden for merchant admin
      if (isMerchantAdmin(
        JSON.parse(sessionStorage.getItem("userInfo") || "{}")?.roleCode,
      )) {
        return;
      }
      try {
        const storedUserInfo = JSON.parse(
          sessionStorage.getItem("userInfo") || "{}",
        );
        const res =
          storedUserInfo?.roleCode === "PROGRAM_ADMIN"
            ? await MerchantApi.getMerchantDataUsingProgramId()
            : await MerchantApi.getAllMerchants();
        setMerchants(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setMerchants([]);
      }
    }
    fetchMerchants();
  }, []);

  // Get the stats for the selected period
  const periodStats = stats?.[selectedPeriod] || {};

  const merchantNameById = useMemo(() => {
    const map = new Map<number, string>();
    merchants.forEach((merchant) => {
      if (merchant?.id != null) {
        map.set(
          merchant.id,
          merchant.merchantName || merchant.name || `Merchant ${merchant.id}`,
        );
      }
    });
    return map;
  }, [merchants]);

  const merchantContributionData = useMemo(
    () =>
      (periodStats?.merchantContributionVOS || []).map((item: any) => ({
        name:
          merchantNameById.get(item.merchantId) || `Merchant ${item.merchantId}`,
        value: Number(item.merchantContribution) || 0,
      })),
    [periodStats?.merchantContributionVOS, merchantNameById],
  );

  const transactionStatusData = useMemo(
    () =>
      (periodStats?.transactionStatusContributionVOS || []).map((item: any) => ({
        name: formatTransactionStatus(item.status),
        status: item.status,
        value: Number(item.contributionPercentage) || 0,
      })),
    [periodStats?.transactionStatusContributionVOS],
  );

  return (
    <div className="space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="w-full sm:w-48">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger>
              <SelectValue placeholder="Select Period" />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((period) => (
                <SelectItem key={period.key} value={period.key}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <DashboardStatsSkeleton />
      ) : stats ? (
        <>
          {/* Top Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricsCard
              title={merchantAdmin ? "Total Programs" : "Total Merchants"}
              value={
                merchantAdmin
                  ? (periodStats.totalPrograms?.toLocaleString() ?? 0)
                  : (periodStats.totalMerchants?.toLocaleString() ?? 0)
              }
              icon={<Gift className="h-5 w-5" />}
            />
            <MetricsCard
              title="Total Transactions"
              value={periodStats.totalTransactions?.toLocaleString() ?? 0}
              icon={<CreditCard className="h-5 w-5" />}
            />
            <MetricsCard
              title="Points Accrued"
              value={periodStats.totalPointsAccrued?.toLocaleString() ?? 0}
              icon={<Star className="h-5 w-5" />}
            />
            <MetricsCard
              title="Revenue"
              value={
                <>
                  {(periodStats.revenue ?? 0).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                  })}{" "}
                  <span className="text-sm font-normal">
                    {currency?.iso3CurrencyCode || ""}
                  </span>
                </>
              }
              icon={<BarChart2 className="h-5 w-5" />}
            />
          </div>

          {/* Charts hidden for merchant admin — only top 4 cards shown */}
          {!merchantAdmin && (
            <>
              {/* Points Accrued Over Time (Area Chart) */}
              <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg shadow border">
                <h2 className="text-xl font-semibold mb-4">
                  Points Accrued Over Time
                </h2>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={Object.entries(
                        periodStats?.monthlyTotalPointsAccruedBreakdown || {},
                      )
                        .map(([date, value]: any) => ({
                          date,
                          points: Number(value) || 0,
                        }))
                        .sort(
                          (a: any, b: any) =>
                            new Date(a.date).getTime() -
                            new Date(b.date).getTime(),
                        )}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tickFormatter={(value: any) => {
                          const d = new Date(value);
                          return isNaN(d.getTime())
                            ? value
                            : format(d, "MMM yyyy");
                        }}
                      />
                      <YAxis tickFormatter={formatYAxisTick} fontSize={12} />
                      <Tooltip
                        formatter={(val: any) => [val, "Points"]}
                        labelFormatter={(label) => label}
                      />
                      <Area
                        type="monotone"
                        dataKey="points"
                        stroke="#2563eb"
                        fill="#93c5fd"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Revenue Over Time (Area Chart) */}
              <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg shadow border">
                <h2 className="text-xl font-semibold mb-4">Revenue Over Time</h2>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={Object.entries(
                        periodStats?.monthlyCashbackAmountBreakdown || {},
                      )
                        .map(([date, value]: any) => ({
                          date,
                          value: Number(value) || 0,
                        }))
                        .sort(
                          (a: any, b: any) =>
                            new Date(a.date).getTime() -
                            new Date(b.date).getTime(),
                        )}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tickFormatter={(value: any) => {
                          const d = new Date(value);
                          return isNaN(d.getTime())
                            ? value
                            : format(d, "MMM yyyy");
                        }}
                      />
                      <YAxis tickFormatter={formatYAxisTick} fontSize={12} />
                      <Tooltip
                        formatter={(val: any) => [
                          `${Number(val).toLocaleString()} ${currency?.iso3CurrencyCode || ""}`.trim(),
                          "Revenue",
                        ]}
                        labelFormatter={(label) => label}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#16a34a"
                        fill="#86efac"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* New vs Repeated Transactions (Stacked Area Chart) */}
              <div className="mt-6 p-4 sm:p-6 bg-white rounded-lg shadow border">
                <h2 className="text-xl font-semibold mb-4">
                  New vs Repeated Transactions Over Time
                </h2>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={Array.from(
                        new Set([
                          ...Object.keys(
                            periodStats?.monthlyRepeatedTransactions || {},
                          ),
                          ...Object.keys(
                            periodStats?.monthlyNewTransactions || {},
                          ),
                        ]),
                      )
                        .map((date: any) => ({
                          date,
                          repeatedTx:
                            Number(
                              (periodStats?.monthlyRepeatedTransactions || {})[
                                date
                              ],
                            ) || 0,
                          newTx:
                            Number(
                              (periodStats?.monthlyNewTransactions || {})[date],
                            ) || 0,
                        }))
                        .sort(
                          (a: any, b: any) =>
                            new Date(a.date).getTime() -
                            new Date(b.date).getTime(),
                        )}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tickFormatter={(value: any) => {
                          const d = new Date(value);
                          return isNaN(d.getTime())
                            ? value
                            : format(d, "MMM yyyy");
                        }}
                      />
                      <YAxis
                        tickFormatter={(v: any) => Number(v).toLocaleString()}
                        fontSize={12}
                      />
                      <Tooltip
                        formatter={(val: any, name: any) => [
                          Number(val).toLocaleString(),
                          name,
                        ]}
                        labelFormatter={(label) => label}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="repeatedTx"
                        name="Repeated"
                        stackId="1"
                        stroke="#22c55e"
                        fill="#86efac"
                        fillOpacity={0.9}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="newTx"
                        name="New"
                        stackId="1"
                        stroke="#3b82f6"
                        fill="#bfdbfe"
                        fillOpacity={0.9}
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Merchant & Transaction Status Pie Charts */}
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
                  <h2 className="text-xl font-semibold mb-4">
                    Merchant-wise Contribution
                  </h2>
                  <div className="h-[320px] w-full">
                    {merchantContributionData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={merchantContributionData}
                            cx="50%"
                            cy="55%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {merchantContributionData.map((_, index) => (
                              <Cell
                                key={`merchant-cell-${index}`}
                                fill={
                                  MERCHANT_PIE_COLORS[
                                    index % MERCHANT_PIE_COLORS.length
                                  ]
                                }
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(val: any) => [
                              `${Number(val).toFixed(2)}%`,
                              "Contribution",
                            ]}
                          />
                          <Legend verticalAlign="top" align="center" />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-500">
                        No merchant contribution data available
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 sm:p-6 bg-white rounded-lg shadow border">
                  <h2 className="text-xl font-semibold mb-4">
                    Transaction Status Distribution
                  </h2>
                  <div className="h-[320px] w-full">
                    {transactionStatusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={transactionStatusData}
                            cx="50%"
                            cy="55%"
                            innerRadius={60}
                            outerRadius={100}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {transactionStatusData.map(
                              (entry: any, index: number) => (
                                <Cell
                                  key={`status-cell-${index}`}
                                  fill={
                                    STATUS_PIE_COLORS[entry.status] ||
                                    MERCHANT_PIE_COLORS[
                                      index % MERCHANT_PIE_COLORS.length
                                    ]
                                  }
                                  stroke="#fff"
                                  strokeWidth={2}
                                />
                              ),
                            )}
                          </Pie>
                          <Tooltip
                            formatter={(val: any) => [
                              `${Number(val).toFixed(2)}%`,
                              "Share",
                            ]}
                          />
                          <Legend verticalAlign="top" align="center" />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-500">
                        No transaction status data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="text-center text-gray-500">No data available</div>
      )}
    </div>
  );
};

export default Dashboard;
